import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'

import { calculRegistrationState } from '@/middlewares/services/home.services'
import { useAuth } from '@/middlewares/auth/auth-context'
import TranslationNS from '@/utils/Constants/TranslationNS'
import useSearchQuery from '@/hooks/useSearchQuery'
import useScrolled from '@/hooks/useScrolled'
import { parseSearchParams, writeSearchParams } from '@/utils/homeSearch'
import EventCard from './EventCard'
import EventCardSkeleton from './EventCard/Skeleton'
import SearchBar from './SearchBar'
import AdvancedSearch from './AdvancedSearch'
import EventsMap from './EventsMap'
import { GetEventTags } from './index.utils'

import './index.scss'

const DEFAULT_PAGE_SIZE = 12
const SKELETON_COUNT = 3

const Home = () => {
    const { t } = useTranslation(TranslationNS.HOME_PAGE)
    const authContext = useAuth()
    const tz = authContext.user.timezone || dayjs.tz.guess()

    const [searchParams, setSearchParams] = useSearchParams()
    const [pageNumber, setPageNumber] = useState(1)

    // The list/map view lives in the URL (`?view=map`) so it survives reloads and
    // can be shared; writeSearchParams leaves the key alone when filters change.
    const showMap = searchParams.get('view') === 'map'

    // The searchable definition is owned by the URL (written by the Navbar
    // search + filters, and by the category buttons below).
    const search = useMemo(() => parseSearchParams(searchParams), [searchParams])

    // Stable key over the search definition, used to detect real search changes.
    const searchKey = useMemo(() => {
        const params = writeSearchParams(new URLSearchParams(), search)
        params.sort()
        return params.toString()
    }, [search])

    const query = useMemo(
        () => ({
            ...search,
            view: showMap ? 'map' : 'list',
            pageNumber,
            pageSize: DEFAULT_PAGE_SIZE,
        }),
        [search, showMap, pageNumber],
    )

    const { isLoading, error, results, hasMore } = useSearchQuery(query)
    const isScrolled = useScrolled()

    // Reset to page 1 whenever the search definition changes (skip first run to
    // avoid a redundant double fetch on mount).
    const firstRun = useRef(true)
    useEffect(() => {
        if (firstRun.current) {
            firstRun.current = false
            return
        }
        setPageNumber(1)
    }, [searchKey])

    const observer = useRef<IntersectionObserver | null>(null)
    const eventsMoreLoader = useCallback(
        (node: HTMLDivElement | null) => {
            if (isLoading) return
            if (observer.current) observer.current.disconnect()
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    setPageNumber((previous) => previous + 1)
                }
            })
            if (node) observer.current.observe(node)
        },
        [isLoading, hasMore],
    )

    const onClickShowMap = () => {
        setPageNumber(1)
        setSearchParams(
            (previous) => {
                if (showMap) previous.delete('view')
                else previous.set('view', 'map')
                return previous
            },
            { replace: true },
        )
    }

    const isEmpty = !isLoading && !error && results.length === 0

    return (
        <>
            <div
                className={`
                    ${
                        !showMap
                            ? `
                                before:content-['']
                                before:fixed
                                before:top-0
                                before:left-0
                                before:right-0
                                before:h-[25vmin]
                                before:bg-[linear-gradient(#ffffffb4,transparent)]
                                before:pointer-events-none
                                before:z-10
                            `
                            : ''
                    }

                    ${
                        // Legacy layout: the map is a fixed full-width panel filling the
                        // viewport under the nav (App.js hides the footer for it).
                        showMap
                            ? `
                                fixed inset-x-0 bottom-0
                                ${isScrolled ? 'top-[85px]' : 'top-(--navbar-height)'}
                                z-20
                                flex flex-col overflow-hidden
                            `
                            : 'pt-(--navbar-height)'
                    }
                `}
            >
                <SearchBar showMap={showMap} onClickShowMap={onClickShowMap} />
                <AdvancedSearch />

                {showMap ? (
                    <div className="home-map-shell flex-1 min-h-0 w-full">
                        <EventsMap events={results} />
                    </div>
                ) : (
                    <div
                        data-tour="home-cards"
                        className={`
                            grid grid-cols-1 min-[601px]:grid-cols-2 min-[981px]:grid-cols-3
                            gap-5
                            w-full mx-auto px-5

                            md:max-w-[min(calc(100%-var(--x003)),var(--x005))]
                            md:px-(--x001)
                        `}
                    >
                        {results.map((competition) => (
                            <EventCard
                                key={competition._id}
                                tz={tz}
                                competition={GetEventTags(competition, t)}
                                registrationStatus={calculRegistrationState(
                                    competition._ticketing,
                                    competition.divisions,
                                    competition.total_participants,
                                )}
                            />
                        ))}
                        {isLoading &&
                            !error &&
                            Array.from({ length: SKELETON_COUNT }, (_, index) => (
                                <EventCardSkeleton key={`skeleton-${index}`} />
                            ))}
                        {isEmpty && (
                            <p className="col-span-full m-0 px-0.5 py-1 text-[12px] text-[#9498a6]">
                                {t('No event matches these filters.')}
                            </p>
                        )}
                        {error && (
                            <p className="col-span-full m-0 px-0.5 py-1 text-[12px] text-[#9498a6]">
                                {t('Oops, an error occurred !')}
                            </p>
                        )}
                        <div className="events-more-loader" ref={eventsMoreLoader}></div>
                    </div>
                )}
            </div>
        </>
    )
}

export default Home
